// Onglets de la candidature côté bailleur : Synthèse (trois résultats), Pièces, Contrôles, Revenus, Rapport.
import { useState } from "react";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Notice, Select, Table, useToast } from "@core/ui";
import { RequirementsView } from "./pieces";
import { byFamily, blockTone, CheckList, countsText, ModesLine, RunLine, SplitChecks, type Block, type Check, type Modes } from "./shared";

type Synthese = {
  application: { id: string; status: string };
  analyse: any;
  blocks: Block[];
  decision: string;
  criteres_obsoletes: boolean;
  dernier_rapport: { version: number; obsolete: boolean } | null;
  modes: Modes;
  notice: string | null;
  version_regles: string;
};

type P = { orgId: string; applicationId: string };

function useRelaunch({ orgId, applicationId }: P) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const relaunch = async () => {
    setBusy(true);
    setError(null);
    try {
      await post(`/api/v1/orgs/${orgId}/applications/${applicationId}/controles/relancer`);
      toast("Contrôles relancés. Les constats précédents sont conservés dans l’historique.");
      invalidate(`/api/v1/orgs/${orgId}/applications/${applicationId}`, `/api/v1/applications/${applicationId}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return { relaunch, busy, error };
}

export function SyntheseTab({ orgId, applicationId }: P) {
  const org = useOrg();
  const q = useGet<Synthese>(`/api/v1/orgs/${orgId}/applications/${applicationId}/controles/synthese`);
  const r = useRelaunch({ orgId, applicationId });
  if (q.isLoading) return <Loading label="Analyse du dossier…" />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  return (
    <div className="stack">
      <div className="row-between">
        <div className="stack-sm">
          <RunLine run={d.analyse} />
          <ModesLine modes={d.modes} />
        </div>
        {org.can("operate") ? (
          <Button variant="secondaire" busy={r.busy} onClick={r.relaunch}>
            Relancer les contrôles
          </Button>
        ) : null}
      </div>
      <ErrorBox error={r.error} />
      {d.notice ? <Notice tone="attention">{d.notice}</Notice> : null}
      {d.criteres_obsoletes ? <Notice tone="attention">Les critères de la campagne ont changé depuis la dernière analyse : relancez les contrôles pour les appliquer.</Notice> : null}
      {d.dernier_rapport?.obsolete ? <Notice tone="info">Le rapport v{d.dernier_rapport.version} ne reflète plus l’état du dossier : une nouvelle pièce ou une correction a été prise en compte.</Notice> : null}
      <div className="controle-blocs">
        {d.blocks.map((b, i) => (
          <Card key={b.block} className="controle-bloc" corne={i === 0 && b.state === "incomplet"}>
            <p className="eyebrow">
              {i + 1}. {b.title}
            </p>
            <p className="controle-bloc-etat">
              <Badge tone={blockTone(b.state)}>{b.state_label}</Badge>
            </p>
            <p className="controle-compte">{countsText(b.counts)}</p>
            <p className="small muted">{b.scope}</p>
            <SplitChecks checks={b.checks} />
          </Card>
        ))}
      </div>
      <Notice tone="info" title="Vous décidez">
        {d.decision}
      </Notice>
    </div>
  );
}

export function PiecesTab({ orgId, applicationId }: P) {
  const org = useOrg();
  return (
    <div className="stack">
      <p className="muted">Pièces attendues selon le rôle et la situation de chaque personne, dans la limite de la liste légale. Ouvrez une pièce pour voir les valeurs lues, leur source, et les saisir si la lecture automatique n’est pas disponible.</p>
      <RequirementsView applicationId={applicationId} orgId={orgId} canWrite={org.can("operate")} />
    </div>
  );
}

const FILTERS = [
  { value: "examiner", label: "À examiner (anomalies, à confirmer, manquants)" },
  { value: "tous", label: "Tous les constats" },
  { value: "conformes", label: "Conformes et informations" },
];

export function ControlesTab({ orgId, applicationId }: P) {
  const q = useGet<{ blocks: Block[]; analyse: any; modes: Modes }>(`/api/v1/applications/${applicationId}/constats`);
  const history = useGet<{ items: any[]; constats_remplaces: number }>(`/api/v1/orgs/${orgId}/applications/${applicationId}/controles/historique`);
  const [filter, setFilter] = useState("examiner");
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const all: Check[] = q.data!.blocks.flatMap((b) => b.checks);
  const shown = all.filter((c) => (filter === "tous" ? true : filter === "conformes" ? ["conforme", "info"].includes(c.status) : !["conforme", "info"].includes(c.status)));
  return (
    <div className="stack">
      <div className="row-between">
        <div className="stack-sm">
          <RunLine run={q.data!.analyse} />
          <ModesLine modes={q.data!.modes} />
        </div>
        <Select label="Afficher" value={filter} options={FILTERS} onChange={(e) => setFilter(e.target.value)} />
      </div>
      {!all.length ? <Empty title="Aucun constat">Les contrôles sont lancés au dépôt de la candidature.</Empty> : null}
      {byFamily(shown).map(([family, checks]) => (
        <section key={family} className="controle-famille">
          <h3>{family}</h3>
          <CheckList checks={checks} />
        </section>
      ))}
      {history.data?.items.length ? (
        <Card title="Historique des analyses">
          <p className="small muted">{history.data.constats_remplaces} constats antérieurs sont conservés, marqués comme remplacés.</p>
          <Table caption="Analyses">
            <thead>
              <tr>
                <th>Date</th>
                <th>Déclenchement</th>
                <th>Règles</th>
                <th>Par</th>
              </tr>
            </thead>
            <tbody>
              {history.data.items.map((r) => (
                <tr key={r.id}>
                  <td>{dateTime(r.started_at)}</td>
                  <td>{{ depot: "Dépôt", relance: "Relance", piece: "Nouvelle pièce ou correction", demonstration: "Démonstration" }[r.trigger as string] ?? r.trigger}</td>
                  <td className="mono">{r.rule_version}</td>
                  <td>{r.author ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}

export function RevenusTab({ orgId, applicationId }: P) {
  const q = useGet<{ actuel: any; historique: any[]; regles: string[] }>(`/api/v1/orgs/${orgId}/applications/${applicationId}/revenus`);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const cur = q.data!.actuel;
  if (!cur) return <Empty title="Pas encore de calcul">Le calcul des revenus est fait avec les contrôles, à partir des valeurs lues ou saisies.</Empty>;
  const c = cur.calcul;
  return (
    <div className="stack">
      <div className="stats">
        <div className="stat">
          <span className="stat-label">Foyer, revenus justifiés</span>
          <span className="stat-value num">{money(c.household_justified_cents)}</span>
          <span className="stat-hint">par mois</span>
        </div>
        <div className="stat">
          <span className="stat-label">Foyer, déclarés non justifiés</span>
          <span className="stat-value num">{money(c.household_unjustified_cents)}</span>
          <span className="stat-hint">ni acquis ni nuls</span>
        </div>
        <div className="stat">
          <span className="stat-label">Garants, revenus justifiés</span>
          <span className="stat-value num">{money(c.guarantors_justified_cents)}</span>
          <span className="stat-hint">présentés à part</span>
        </div>
      </div>
      <p className="small muted">
        Calcul du {date(c.computed_on)} · règles <span className="mono">{c.rule_version}</span>
      </p>
      {c.persons.map((p: any) => (
        <Card key={p.applicant_id} title={`${p.name}`} actions={<Badge tone={p.role === "garant" ? "neutre" : "encre"}>{p.role === "garant" ? "Garant" : "Foyer"}</Badge>}>
          {p.components.length ? (
            <Table caption={`Revenus de ${p.name}`}>
              <thead>
                <tr>
                  <th>Composante</th>
                  <th>Méthode</th>
                  <th className="right">Mensuel retenu</th>
                </tr>
              </thead>
              <tbody>
                {p.components.map((comp: any) => (
                  <tr key={comp.income_type}>
                    <td>
                      {comp.label} {comp.uncertain ? <Badge tone="attention">Montant incertain</Badge> : null}
                    </td>
                    <td className="small">{comp.method}</td>
                    <td className="right num">{money(comp.monthly_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
          {p.components.some((comp: any) => comp.lines.length) ? (
            <details className="controle-details">
              <summary>Détail pièce par pièce</summary>
              <Table caption="Lignes du calcul">
                <thead>
                  <tr>
                    <th>Ligne</th>
                    <th>Période</th>
                    <th className="right">Montant</th>
                    <th>Prise en compte</th>
                  </tr>
                </thead>
                <tbody>
                  {p.components.flatMap((comp: any) =>
                    comp.lines.map((l: any, i: number) => (
                      <tr key={`${comp.income_type}-${i}`}>
                        <td>{l.label}</td>
                        <td>{l.period ?? "—"}</td>
                        <td className="right num">{money(l.amount_cents)}</td>
                        <td className="small">{l.included ? "Incluse" : `Exclue : ${l.reason}`}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </Table>
            </details>
          ) : null}
          {p.notes.map((n: string) => (
            <p key={n} className="small muted">
              {n}
            </p>
          ))}
        </Card>
      ))}
      <Card title="Règles de calcul">
        <ul>
          {q.data!.regles.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </Card>
      {q.data!.historique.length > 1 ? (
        <details className="controle-details">
          <summary>Calculs précédents</summary>
          <Table caption="Historique des calculs">
            <thead>
              <tr>
                <th>Date</th>
                <th className="right">Foyer justifié</th>
                <th className="right">Garants</th>
                <th>Règles</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.historique.map((h) => (
                <tr key={h.id}>
                  <td>{dateTime(h.created_at)}</td>
                  <td className="right num">{money(h.foyer_justifie_cents)}</td>
                  <td className="right num">{money(h.garants_justifie_cents)}</td>
                  <td className="mono">{h.rule_version}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </details>
      ) : null}
    </div>
  );
}

export function RapportTab({ orgId, applicationId }: P) {
  const org = useOrg();
  const path = `/api/v1/orgs/${orgId}/applications/${applicationId}/rapports`;
  const q = useGet<{ items: any[] }>(path);
  const invalidate = useInvalidate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted">Le rapport présente, daté et versionné, les pièces examinées, les trois résultats avec leurs sources, le calcul des revenus, les critères appliqués et les limites des contrôles. Les références sensibles sont masquées.</p>
        {org.can("operate") ? (
          <Button
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await post(path);
                toast("Rapport généré.");
                invalidate(path, "/api/v1/orgs/");
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Générer un rapport
          </Button>
        ) : null}
      </div>
      <ErrorBox error={error} />
      {q.isLoading ? <Loading /> : null}
      {q.data && !q.data.items.length ? <Empty title="Aucun rapport">Générez un rapport après l’analyse du dossier.</Empty> : null}
      {q.data?.items.length ? (
        <Table caption="Versions du rapport">
          <thead>
            <tr>
              <th>Version</th>
              <th>Établi le</th>
              <th>Par</th>
              <th>État</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((r) => (
              <tr key={r.id}>
                <td>v{r.version}</td>
                <td>{dateTime(r.created_at)}</td>
                <td>{r.author ?? "—"}</td>
                <td>{r.obsolete_at ? <Badge tone="attention">Obsolète depuis le {date(r.obsolete_at)}</Badge> : <Badge tone="succes">À jour</Badge>}</td>
                <td className="right">
                  {r.fichier ? (
                    <a href={`${r.fichier}?telecharger=true`} className="bv-btn bv-btn-secondaire bv-btn-petit">
                      Télécharger le PDF
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </div>
  );
}
